import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { Modal, Spin, Alert } from "antd";
import { TransactionAttachmentFile } from "@lib/types";
import { apiOpenTransactionFile } from "@lib/api/transaction";
import { useIntl } from "react-intl";
import _ from "lodash";

type FilePreviewModalProps = {
  visible: boolean;
  file: TransactionAttachmentFile | null;
  onClose: () => void;
};

function FilePreviewModal({ visible, file, onClose }: FilePreviewModalProps) {
  const { messages } = useIntl();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !file) {
      setBlobUrl(null);
      setError(null);
      setLoading(false);
      return;
    }

    const loadFile = async () => {
      setLoading(true);
      setError(null);

      try {
        if (file.data) {
          const response = await apiOpenTransactionFile(file.data);
          const blob = new Blob([response], { type: file.type });
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
        } else {
          const url = URL.createObjectURL(file as File);
          setBlobUrl(url);
        }
      } catch (err) {
        setError(messages["common.fileLoadingError"] as string);
        console.error("File load error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadFile();

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [visible, file?.uid, file?.data?.id]);

  const isImage = useMemo(() => file?.type?.startsWith("image/"), [file?.type]);
  const isPdf = useMemo(() => file?.type === "application/pdf", [file?.type]);

  const renderContent = useCallback(() => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>
            {messages["common.fileLoading"] as string}
          </p>
        </div>
      );
    }

    if (error) {
      return <Alert message={error} type="error" showIcon />;
    }

    if (!blobUrl) {
      return (
        <Alert
          message={messages["common.fileNotFound"] as string}
          type="warning"
          showIcon
        />
      );
    }

    if (isImage) {
      return (
        <img
          src={blobUrl}
          alt={file?.name}
          style={{
            width: "100%",
            height: "auto",
            maxHeight: "80vh",
            objectFit: "contain",
          }}
        />
      );
    }

    if (isPdf) {
      return (
        <iframe
          src={blobUrl}
          style={{
            width: "100%",
            height: "80vh",
            border: "none",
          }}
          title={file?.name}
        />
      );
    }

    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <p>{messages["common.fileNotPreviewable"] as string}</p>
        <a
          href={blobUrl}
          download={file?.name}
          style={{
            display: "inline-block",
            padding: "10px 20px",
            backgroundColor: "#1890ff",
            color: "white",
            textDecoration: "none",
            borderRadius: "4px",
            marginTop: "16px",
          }}
        >
          {messages["common.downloadFile"] as string}
        </a>
      </div>
    );
  }, [loading, error, blobUrl, isImage, isPdf, file?.name]);

  const modalTitle = useMemo(
    () => file?.name || (messages["common.filePreview"] as string),
    [file?.name],
  );

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onClose}
      footer={null}
      width="90%"
      style={{ top: 20 }}
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  );
}

export default memo(FilePreviewModal, (prevProps, nextProps) => {
  return (
    prevProps.visible === nextProps.visible &&
    _.isEqual(prevProps.file, nextProps.file)
  );
});
